import { action, computed, observable, makeObservable } from 'mobx';
import { AxiosRequestConfig, AxiosResponse, Canceler } from 'axios';
import { formatDate } from '../helpers/walletTransaction';
import { defaultPagination } from 'src/constants/main';
import { IPagination, LoadingStatus } from 'src/types/main';
import { IWalletTransactionFilters } from 'src/view/screens/WalletTransactionLogScreen/screens/WTLogScreen/types/walletTransaction';
import client from 'src/services/client';
import { defaultWalletTransactionFilters } from '~/view/screens/WalletTransactionLogScreen/screens/WTLogScreen/constants/walletTransactionRow';
import { getCancelToken } from 'src/utils/helpers';
import logger from '~/services/logger';
import { localeStore } from '~/mobx/localesStore';
import wms from '~/services/wms';

export interface IWalletTransaction {
  author_id: number;
  object_id: number;
  deposit_date: string | null;
  balance_type: number;
  transaction_type: number;
  transaction_currency: string | null;
  reason: string | number | null;
  order_number: number | null;
  expiration_date: string | null;
  wallet_id: number;
  transaction_id: number;
  author_name: string;
  author_phone: string | null;
  object: string;
  object_phone: string;
  transaction_amount: number;
}

class WalletTransactionStore {
  constructor() {
    makeObservable(this);
  }

  @observable pagination: IPagination<IWalletTransaction> = defaultPagination;
  @observable filters: IWalletTransactionFilters = defaultWalletTransactionFilters;
  @observable public exportLoadingStatus: LoadingStatus = LoadingStatus.Init;

  WTLogExportAbortController = new AbortController();

  cancelStack: {
    [key: string]: Canceler;
  } = {};

  /**
   * Are there no existed wallets transactions in system
   */
  get isEmptyWalletTransactionList() {
    return !this.pagination.loading && !this.pagination.data.length;
  }

  get countLines() {
    return this.pagination.data.length;
  }

  @computed get isLoading(): boolean {
    return Boolean(this.pagination.loading || this.pagination.refreshing);
  }

  @action setFilters = (filters: Partial<IWalletTransactionFilters>) => {
    this.filters = { ...this.filters, ...filters };
    this.fetchWalletTransactionList();
  };

  @action fetchWalletTransactionList = async (page: number = 1, initLoading: boolean = true) => {
    try {
      const cancelId = 'fetchWalletTransactionList';
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      // skip loading if its already initialized
      if (initLoading) {
        this.pagination.loading = true;
        this.pagination.refreshing = true;
      }

      const pagination = await client.get('v3/wallets/transactions/log', {
        params: {
          page,
          ...this.getParams(),
        },
        headers: { 'Accept-Language': localeStore.language },
        cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)),
      });

      this.pagination = {
        ...pagination,
        loading: false,
        refreshing: false,
      };
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination.loading = false;
      this.pagination.refreshing = false;
      logger.error({ error, place: 'walletTransactionStore fetchWalletTransactionList' });
    }
  };

  clear = () => {
    this.pagination = defaultPagination;
    this.filters = defaultWalletTransactionFilters;
  };

  private getParams = () => {
    return {
      user_id: this.filters.userId,
      sort_by: this.filters.sortBy,
      sort_dir: this.filters.sortDir,
      filter_date_start: formatDate(this.filters.startDate),
      filter_date_end: formatDate(this.filters.endDate),
      filter_balance_type: this.filters.balance_type,
      filter_transaction_type: this.filters.transaction_type,
      export_csv_url: window.IS_MOBILE ? 1 : 0,
    };
  };

  @action downloadExportedDocument = async () => {
    this.exportLoadingStatus = LoadingStatus.Loading;

    const WTLogExportAbortController = new AbortController();
    this.WTLogExportAbortController = WTLogExportAbortController;

    try {
      const config: AxiosRequestConfig = {
        signal: WTLogExportAbortController.signal,
        params: this.getParams(),
      };

      await this.downloadFile('v3/wallets/transactions/log/export', config);

      this.exportLoadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      if (WTLogExportAbortController.signal.aborted) {
        this.exportLoadingStatus = LoadingStatus.Success;
      } else {
        this.exportLoadingStatus = LoadingStatus.Error;
        logger.error({ error, place: 'WalletTransactionStore downloadExportedDocument', showModal: true });
      }
    }
  };

  /**
   * Download file from API
   *
   * @param {string} config - Axios request config.
   * @param {string} filename - File name for downloading.
   * @param {string} filetype - File type for Blob.
   */
  @action downloadFile = async (url: string, config: AxiosRequestConfig) => {
    const response: AxiosResponse = await client.get(
      url,
      {
        ...config,
        responseType: window.IS_MOBILE ? 'text' : 'blob',
        timeout: 5 * 60 * 1000, // For long requests
      },
      true
    );

    if (window.IS_MOBILE) {
      wms.downloadFile(response.data);
    } else {
      const filename = response.headers['content-disposition'].split(';')[1].split('=')[1].replace(/"/g, '').trim();
      const type = response.headers['content-type'];

      const blob = new Blob([response.data], { type });
      const link = document.createElement('a');
      const objectURL = window.URL.createObjectURL(blob);

      link.href = objectURL;
      link.download = filename;
      link.style.display = 'none';
      link.innerText = 'download';

      document.body.appendChild(link);
      link.click();

      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(objectURL);
    }
  };
}

const walletTransactionStore = new WalletTransactionStore();

export default walletTransactionStore;
